import { Box, Typography, Divider } from '@mui/material'
import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

export default function RegisterConfirmation() {
    const navigate = useNavigate();

    // Constants extracted from the JSX
    const CONTENT = {
        title: "Great!",
        subtitle: "You're Almost There.",
        steps: [
            "We've sent a confirmation email with your registration details.\n(if you don't see it, check your spam folder)",
            "Share more about your organization and required features to customize your experience. Click the link below to proceed."
        ],
        button: "Complete Your Profile Now",
        footer: "We're excited to have you on board! Get ready to explore a seamless experience tailored to your needs. – The RouteEye Team"
    };

    const [animate, setAnimate] = useState(false);

    useEffect(() => {
        // Trigger animation after component mounts
        const timer = setTimeout(() => {
            setAnimate(true);
        }, 300);

        return () => clearTimeout(timer);
    }, []);

    // Handle navigation
    const handleNavigate = () => {
        navigate('/registermore');
    };

    // Function to render styled parts of the text
    const renderStyledStepText = (text, index) => {
        if (index === 0) {
            const parts = text.split('(');
            if (parts.length > 1) {
                return (
                    <>
                        {parts[0]}
                        <Typography component="span" sx={{
                            fontFamily: "Roboto",
                            fontWeight: 300,
                            fontSize: "0.9rem",
                            lineHeight: "100%",
                            display: "inline"
                        }}>
                            ({parts[1]}
                        </Typography>
                    </>
                );
            }
        } else if (index === 1) {
            const parts = text.split('Click the link below');
            if (parts.length > 1) {
                return (
                    <>
                        {parts[0]}
                        <Typography component="span" sx={{
                            fontStyle: "italic",
                            display: "inline"
                        }}>
                            Click the link below{parts[1]}
                        </Typography>
                    </>
                );
            }
        }
        return text;
    };

    return (
        <Box sx={{
            padding: { xs: "8px", sm: "16px", md: "24px" },
            backgroundColor: "#EEEEEE",
            height: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
        }}>
            <Box sx={{
                width: "100%",
                maxWidth: "1200px",
                margin: "0 auto",
                borderRadius: "20px",
                padding: { xs: "16px", sm: "24px", md: "40px" },
                gap: { xs: "8px", sm: "16px", md: "24px" },
                background: "#FFFFFF",
                border: "1px solid #A5DCF3",
                boxShadow: "-2px 2px 5px 0px #00000040",
                display: "flex",
                flexDirection: "column",
                maxHeight: "95vh",
                overflow: "auto",
                // Hide scrollbar for Chrome, Safari and Opera
                "&::-webkit-scrollbar": {
                    display: "none"
                },
                // Hide scrollbar for IE, Edge and Firefox
                msOverflowStyle: "none",  // IE and Edge
                scrollbarWidth: "none",   // Firefox
            }}>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "12px" }}>
                    {/* Green circle with tick animation */}
                    <Box sx={{ width: { xs: "60px", sm: "80px" }, height: { xs: "60px", sm: "80px" }, position: "relative" }}>
                        <svg
                            width="100%"
                            height="100%"
                            viewBox="0 0 100 100"
                        >
                            {/* Circle animation - filled green */}
                            <circle
                                cx="50"
                                cy="50"
                                r="46"
                                fill="#008C23"
                                style={{
                                    transform: animate ? 'scale(1)' : 'scale(0)',
                                    opacity: animate ? 1 : 0,
                                    transition: 'transform 0.7s ease-out, opacity 0.7s ease-out',
                                    transformOrigin: 'center'
                                }}
                            />

                            {/* Checkmark animation */}
                            <path
                                d="M28,50 L45,67 L72,40"
                                fill="none"
                                stroke="white"
                                strokeWidth="8"
                                strokeDasharray="68"
                                strokeDashoffset={animate ? "0" : "68"}
                                style={{
                                    transition: 'stroke-dashoffset 0.7s ease-out 0.5s'
                                }}
                            />
                        </svg>
                    </Box>
                    <Typography sx={{
                        fontFamily: "Roboto",
                        fontWeight: "600",
                        fontSize: { xs: "20px", sm: "24px", md: "28px" },
                        color: "#183059",
                        padding: "0px 8px",
                        textAlign: "center"
                    }}>
                        {CONTENT.title}
                        <span style={{
                            fontFamily: "Roboto",
                            fontWeight: 400,
                            marginLeft: "8px",
                        }}>
                            {CONTENT.subtitle}
                        </span>
                    </Typography>
                    <Typography variant="body1" sx={{
                        textAlign: "center",
                        maxWidth: { xs: "100%", md: "90%" },
                        fontSize: { xs: "0.9rem", sm: "1rem" }
                    }}>
                        Thank you for registering with <Typography component="span" fontWeight="bold">RouteEye</Typography>. Follow the steps below to get started.
                    </Typography>
                </Box>

                <Box sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                    padding: { xs: "0px 4px", sm: "0px 16px", md: "0px 80px" },
                    width: "100%"
                }}>
                    {CONTENT.steps.map((step, index) => (
                        <Box key={index} sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: { xs: "8px", sm: "12px" },
                            padding: { xs: "6px", sm: "8px", md: "12px" },
                            borderRadius: "8px"
                        }}>
                            <Box sx={{
                                width: { xs: "28px", sm: "32px" },
                                height: { xs: "28px", sm: "32px" },
                                borderRadius: "50%",
                                bgcolor: "#647490",
                                color: "#FFFFFF",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontWeight: "bold",
                                flexShrink: 0,
                                fontSize: { xs: "12px", sm: "14px" }
                            }}>{index + 1}</Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="body1" sx={{
                                    fontSize: { xs: "0.85rem", sm: "0.95rem" },
                                    lineHeight: 1.4
                                }}>
                                    {step.split('\n').map((line, i) => (
                                        <React.Fragment key={i}>
                                            {i === 0 ? renderStyledStepText(line, index) : line}
                                            {i < step.split('\n').length - 1 && <br />}
                                        </React.Fragment>
                                    ))}
                                </Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>

                <Box sx={{
                    display: "flex",
                    justifyContent: "center",
                    mt: { xs: 1, sm: 2 }
                }}>
                    <Typography
                        onClick={handleNavigate}
                        sx={{
                            color: "#20A8E0",
                            borderRadius: "8px",
                            padding: { xs: "6px 12px", sm: "8px 16px", md: "10px 20px" },
                            fontWeight: "500",
                            "&:hover": {
                                color: "#1890C0",
                                cursor: "pointer"
                            },
                            fontFamily: "Roboto",
                            fontSize: { xs: "16px", sm: "18px", md: "20px" },
                            textAlign: "center"
                        }}
                    >
                        {CONTENT.button}
                    </Typography>
                </Box>

                <Box sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: { xs: 1, sm: 1.5 },
                    padding: { xs: "0px 12px", sm: "0px 24px", md: "0px 40px" },
                    mt: { xs: 1, sm: 2 }
                }}>
                    <Divider sx={{
                        width: "100%",
                        backgroundColor: "#E8EAEE",
                        height: "1px"
                    }} />

                    <Typography
                        variant="body2"
                        sx={{
                            fontFamily: "Roboto",
                            fontWeight: 200,
                            fontStyle: "italic",
                            fontSize: { xs: "14px", sm: "16px", md: "18px" },
                            lineHeight: "120%",
                            color: "#1F242E",
                            textAlign: "center",
                            maxWidth: { xs: "100%", md: "90%" }
                        }}
                    >
                        {CONTENT.footer}
                    </Typography>
                </Box>
            </Box>
        </Box>
    )
}